/**
 * TanStack Query hooks for Signatures
 */

import type { SignaturePublic, UpdateSignatureRequest } from '@garden-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signaturesApi } from '../services/organizationSettingsApi';

export const signatureQueryKeys = {
  all: ['signatures'] as const,
  list: (includeInactive?: boolean) =>
    [...signatureQueryKeys.all, 'list', { includeInactive }] as const,
  detail: (id: string) => [...signatureQueryKeys.all, 'detail', id] as const,
};

/**
 * Fetch all signatures
 */
export function useSignatures(includeInactive = false) {
  return useQuery({
    queryKey: signatureQueryKeys.list(includeInactive),
    queryFn: async () => {
      const response = await signaturesApi.list(includeInactive);
      return response.data as SignaturePublic[];
    },
  });
}

/**
 * Fetch a single signature
 */
export function useSignature(id: string) {
  return useQuery({
    queryKey: signatureQueryKeys.detail(id),
    queryFn: async () => {
      const response = await signaturesApi.get(id);
      return response.data as SignaturePublic;
    },
    enabled: !!id,
  });
}

/**
 * Upload a new signature
 */
export function useUploadSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, name, title }: { file: File; name: string; title?: string }) => {
      const response = await signaturesApi.upload(file, name, title);
      return response.data as SignaturePublic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: signatureQueryKeys.all,
      });
    },
  });
}

/**
 * Update a signature
 */
export function useUpdateSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSignatureRequest }) => {
      const response = await signaturesApi.update(id, data);
      return response.data as SignaturePublic;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: signatureQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: signatureQueryKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Delete a signature
 */
export function useDeleteSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await signaturesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: signatureQueryKeys.all,
      });
    },
  });
}

/**
 * Get signature image URL
 */
export function useSignatureImageUrl(id: string) {
  return signaturesApi.getImageUrl(id);
}
